import { IconCaretDownFilled, IconCaretRightFilled, IconFolderPlus, IconSearch, IconUsers } from '@tabler/icons-react';
import { MultiBackend, Tree, getBackendOptions, useDragOver } from '@minoru/react-dnd-treeview';
import React, { useEffect, useState } from 'react';
import { Button, Grid, Modal, TextInput } from '@mantine/core';
import { DndProvider } from 'react-dnd';
import { useParams } from 'react-router';
import { generatedContentSideIcons } from '../../constants/GeneratedContent';
import { FilterListByName } from '../../constants/filterListByName';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';

const CustomNode = ({ node, depth, onToggle, isOpen, selected }) => {
  const isSelected = selected === node.id.toString();
  const { accountId, domainId } = useParams();
  const indent = depth * 24;

  let channel = node.marketingChannel;
  if (channel === 'folder' && isOpen) {
    channel = 'folderOpen';
  }
  const IconComponent = generatedContentSideIcons[channel];

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  const dragOverProps = useDragOver(node.id, isOpen, onToggle);

  return (
    <div
      className={`tree-node ai-hub-tree-row ${isSelected ? 'font-weight-bold text-primary' : ''}`}
      onClick={handleToggle}
      style={{ paddingInlineStart: indent, color: isSelected ? undefined : 'var(--mantine-color-dimmed)' }}
      {...dragOverProps}
    >
      {node.droppable && (
        <div style={{ paddingRight: 'var(--mantine-spacing-xs)' }}>
          {isOpen ? <IconCaretDownFilled size={14} /> : <IconCaretRightFilled size={14} />}
        </div>
      )}

      <div>
        {IconComponent && <IconComponent size={14} />}
      </div>

      <div style={{ paddingLeft: 'var(--mantine-spacing-xs)' }}>
        {node.marketingChannel !== 'section' ? (
          <a
            href={`#/accounts/${accountId}/domains/${domainId}/generatedContent/${node.id}`}
            className={isSelected ? 'text-primary' : ''}
            style={{ color: isSelected ? undefined : 'var(--mantine-color-dimmed)' }}
          >
            {node.text} {node.shared && <IconUsers size={14} />}
          </a>
        ) : (
          <span className={isSelected ? 'text-primary' : ''} style={{ color: isSelected ? undefined : 'var(--mantine-color-dimmed)' }}>{node.text}</span>
        )}
      </div>
    </div>
  );
};
const parseTreeData = (generatedContents) => {
  if (!generatedContents) {
    return { treeData: [], sectionIds: [] };
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const groups = {
    today: [],
    yesterday: [],
    last7Days: [],
    last30Days: [],
    older: [],
    folders: {},
  };

  generatedContents.forEach((content) => {
    const editedDate = new Date(content.updatedAt);
    const startOfEditedDate = new Date(editedDate.getFullYear(), editedDate.getMonth(), editedDate.getDate());
    const diffInDays = Math.floor((startOfToday - startOfEditedDate) / (1000 * 60 * 60 * 24));

    // Grouping documents by edited date
    const conditions = [
      { check: diffInDays === 0, group: 'today' },
      { check: diffInDays === 1, group: 'yesterday' },
      { check: diffInDays > 1 && diffInDays <= 7, group: 'last7Days' },
      { check: diffInDays > 7 && diffInDays <= 30, group: 'last30Days' },
      { check: diffInDays > 30, group: 'older' },
    ];
    const groupKey = conditions.find((condition) => condition.check)?.group;
    if (groupKey) {
      groups[groupKey].push(content);
    }
    // Group documents by folders
    if (content.parentId) {
      if (!groups.folders[content.parentId]) {
        groups.folders[content.parentId] = [];
      }
      groups.folders[content.parentId].push(content);
    }
  });

  const sections = [
    { id: 'today', text: 'Today', droppable: false, marketingChannel: 'section' },
    { id: 'yesterday', text: 'Yesterday', droppable: false, marketingChannel: 'section' },
    { id: 'last7Days', text: 'Last 7 Days', droppable: false, marketingChannel: 'section' },
    { id: 'last30Days', text: 'Last 30 Days', droppable: false, marketingChannel: 'section' },
    { id: 'older', text: 'Older Than 30 Days', droppable: false, marketingChannel: 'section' },
  ];

  let treeData = [];
  let sectionIds = [];

  // The following handling ensures the documents remain in their original folders
  // but they are also displayed in the date-specific sections
  const addContentToTreeData = (content, parent, isInDateSection) => {
    const isSection = content.marketingChannel === 'section';

    treeData.push({
      id: content.id,
      parent: parent,
      text: isSection ? content.text : content.name,
      name: content.name,
      type: content.marketingChannel,
      marketingChannel: content.marketingChannel,
      shared: content.shared,
      droppable: content.marketingChannel === 'folder',
      isInDateSection,
    });
  };

  Object.keys(groups.folders).forEach((folderId) => {
    groups.folders[folderId].forEach((content) => {
      addContentToTreeData(content, content.parentId, false);
    });
  });

  sections.forEach((section) => {
    const groupKey = section.id;
    if (groups[groupKey]?.length > 0) {
      addContentToTreeData({ ...section, parent: 0 }, 0, false);
      sectionIds.push(section.id);
      groups[groupKey].forEach((content) => {
        addContentToTreeData(content, groupKey, true);
      });
    }
  });

  return { treeData, sectionIds };
};

const GeneratedContentSideBar = ({ generatedContents, refreshContents, createButton }) => {
  const [treeData, setTreeData] = useState(parseTreeData(generatedContents).treeData);
  const { accountId, domainId, generatedContentId } = useParams();
  const [userError, setUserError] = useState();
  const [newFolderName, setNewFolderName] = useState();
  const fetch = useFetch();

  useEffect(() => {
    setTreeData(parseTreeData(generatedContents).treeData);
  }, [generatedContents]);

  const searchHandler = (term) => {
    if (term === '') {
      setTreeData(parseTreeData(generatedContents).treeData);
    } else {
      setTreeData(() => {
        const filteredData = FilterListByName(term, generatedContents);
        return parseTreeData(filteredData).treeData;
      });
    }
  };

  const generatedContent = generatedContents.find((g) => g.id.toString() === generatedContentId);

  const findHierarchy = (contentId, contents, result = []) => {
    result.push(contentId.id);

    const parent = contents.find((e) => e.id === contentId.parent);
    if (parent) {
      findHierarchy(parent, contents, result);
    }

    return result;
  };

  const findParents = (contentId, contents) => {
    const element = contents.find((e) => e.id === contentId);
    if (!element) return [];

    return findHierarchy(element, contents);
  };

  const handleDrop = (newTree, { dragSource, dropTarget }) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/generatedContent/${dragSource.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        parentId: dropTarget ? dropTarget.id : null,
      }),
    })
      .then(setTreeData(newTree))
      .catch(setUserError);
  };

  const createFolder = () => {
    let parentId = null;
    if (generatedContent?.marketingChannel === 'folder') {
      parentId = generatedContent.id;
    }

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/generatedContent`, {
      method: 'POST',
      body: JSON.stringify({
        name: newFolderName,
        marketingChannel: 'folder',
        parentId,
      }),
    })
      .then(() => {
        refreshContents();
        setNewFolderName(undefined);
      })
      .catch(setUserError);
  };

  const sectionIds = parseTreeData(generatedContents).sectionIds;

  return (
    <>
      {userError && <ErrorMessage message={userError.message} />}
      <div style={{ display: 'flex', gap: 'var(--mantine-spacing-xs)' }}>
        <div style={{ flex: 1 }}>{createButton}</div>
        <Button variant="default" onClick={() => setNewFolderName('New folder ' + new Date().getTime())}>
          <IconFolderPlus size={14} />
        </Button>
      </div>
      <TextInput
        onKeyUp={(e) => searchHandler(e.target.value)}
        type="search"
        placeholder="Search..."
        aria-label="Search"
        rightSection={<IconSearch size={16} />}
        mt="xs"
      />
      <hr />

      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <Tree
          tree={treeData}
          rootId={0}
          onDrop={handleDrop}
          sort={false}
          render={(node, { depth, isOpen, onToggle }) => (
            <CustomNode node={node} depth={depth} isOpen={isOpen} selected={generatedContentId} onToggle={onToggle} />
          )}
          classes={{
            root: 'ai-hub-tree',
            draggingSource: 'ai-hub-tree-drag-source',
            dropTarget: 'ai-hub-tree-drop-target',
          }}
          initialOpen={findParents(generatedContent?.id, treeData) && sectionIds}
        />
      </DndProvider>

      <Modal opened={newFolderName !== undefined} onClose={() => setNewFolderName(undefined)} title="New Folder">
        <Grid mb="md">
          <Grid.Col span={{ base: 12 }}>
            <TextInput size="lg" value={newFolderName || ''} onChange={(e) => setNewFolderName(e.target.value)} />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={{ base: 12 }}>
            <Button fullWidth size="lg" onClick={createFolder}>
              <IconFolderPlus size={14} /> Create
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>
    </>
  );
};

export default GeneratedContentSideBar;
